import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { TocItem } from "../types";

function useActiveHeading(toc: TocItem[]) {
  const [active, setActive] = useState(toc[0]?.id ?? "");
  useEffect(() => {
    setActive(toc[0]?.id ?? "");
    const elements = toc.map((item) => document.getElementById(item.id)).filter(Boolean) as HTMLElement[];
    if (!elements.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-18% 0px -68% 0px", threshold: [0, 1] },
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [toc]);
  return active;
}

function TocLinks({ toc, active, onSelect }: { toc: TocItem[]; active: string; onSelect?: () => void }) {
  return (
    <ol className="toc-list">
      {toc.map((item) => (
        <li key={item.id} style={{ "--toc-depth": Math.max(0, item.depth - 2) } as React.CSSProperties}>
          <a href={`#${encodeURIComponent(item.id)}`} data-active={active === item.id || undefined} onClick={onSelect}>{item.text}</a>
        </li>
      ))}
    </ol>
  );
}

export function DesktopToc({ toc }: { toc: TocItem[] }) {
  const active = useActiveHeading(toc);
  if (!toc.length) return <aside className="desktop-toc empty" aria-label="本页目录"><span>本页没有小节</span></aside>;
  return (
    <aside className="desktop-toc" aria-label="本页目录">
      <p>ON THIS PAGE</p>
      <div className="toc-track"><TocLinks toc={toc} active={active} /></div>
    </aside>
  );
}

export function MobileToc({ toc }: { toc: TocItem[] }) {
  const active = useActiveHeading(toc);
  const [open, setOpen] = useState(false);
  const current = useMemo(() => toc.find((item) => item.id === active)?.text ?? toc[0]?.text, [active, toc]);
  if (!toc.length) return null;
  return (
    <div className="mobile-toc" data-open={open || undefined}>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span>本页目录</span>
        <strong>{current}</strong>
        <ChevronDown size={17} />
      </button>
      {open && <div className="mobile-toc-panel"><TocLinks toc={toc} active={active} onSelect={() => setOpen(false)} /></div>}
    </div>
  );
}
